import GridDeleteIcon from "@mui/icons-material/Delete";
import { MenuItem, Select } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import React from "react";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { useGetPromptPlaygroundUserSessionsQuery } from "../../../../slices/AIHub.slice";
import { AI_PLAYGROUND_CONSTANTS as CONSTANTS } from "../../AIPlayground.constants";
import { SessionsTableProps } from "../../Interfaces";

export const SessionsTable: React.FC<SessionsTableProps> = ({
  rows,
  onFieldChange,
  openSessionPanel,
  selectedForDeletion
}) => {
  const { isLoading } = useGetPromptPlaygroundUserSessionsQuery();
  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      width: 400,
      sortable: true,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <a
          style={{
            cursor: "pointer",
            textOverflow: "ellipsis",
            color: "rgb(0, 120, 212)",
            whiteSpace: "nowrap",
            wordWrap: "break-word"
          }}
          onClick={() => {
            openSessionPanel(params.row.id); // Open session panel with the selected row data
          }}
          title={params.row.title || "N/A"}
        >
          {params.row.title}
        </a>
      )
    },
    {
      field: "description",
      headerName: "Description",
      width: 400,
      sortable: true,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <span
          style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", wordWrap: "break-word", cursor: "default" }}
          title={params.row.description || "N/A"}
        >
          {params.row.description || "N/A"}
        </span>
      )
    },
    {
      field: "isActiveSchedule",
      headerName: "Status",
      sortable: true,
      editable: false,
      type: "boolean",
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Select
          value={params.row.isActiveSchedule === true ? "active" : "inactive"}
          onChange={(event) => {
            const newValue = event.target.value === "active";
            onFieldChange("isActiveSchedule", newValue, params.row.id);
          }}
          variant="standard"
          sx={{
            fontSize: 14,
            "&:before": { borderBottom: "none" },
            "&:after": { borderBottom: "none" }
          }}
        >
          {CONSTANTS.STATUSES.map((option: string) => (
            <MenuItem key={option} value={option}>
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </MenuItem>
          ))}
        </Select>
      )
    },
    {
      field: "scheduleFrequency",
      headerName: "Frequency",
      width: 250,
      sortable: true,
      editable: false,
      renderCell: (params: GridRenderCellParams) =>
        !params.row.isActiveSchedule ? (
          <span>{params.row.scheduleFrequency}</span>
        ) : (
          <Select
            disabled={!params.row.isActiveSchedule}
            value={params.row.scheduleFrequency}
            onChange={(event) => {
              onFieldChange("scheduleFrequency", event.target.value, params.row.id);
            }}
            variant="standard"
            sx={{
              fontSize: 14,
              "&:before": { borderBottom: "none" }, // Remove underline (default)
              "&:after": { borderBottom: "none" } // Remove underline (active)
            }}
          >
            {CONSTANTS.SCHEDULE_FREQUENCY_OPTIONS.map((frequency: { key: string; text: string }) => (
              <MenuItem key={frequency.key} value={frequency.key}>
                {frequency.text}
              </MenuItem>
            ))}
          </Select>
        )
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      sortable: false,
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <GridDeleteIcon
          style={{ cursor: "pointer", color: "red", fontSize: 20, height: "100%" }}
          onClick={() => {
            onFieldChange("delete", "", params.row.id);
          }}
          titleAccess={selectedForDeletion?.includes(params.row.id) ? "Undo Delete" : "Delete"}
        />
      )
    }
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={{
        count: rows.length,
        values: [...rows]
      }}
      disableColumnFilter
      disableColumnMenu
      getRowId={(row) => row.id}
      getRowClassName={(params) => (selectedForDeletion?.includes(params.row?.id) ? "row-to-delete" : "")}
      loading={isLoading}
      sx={{
        outline: "none",
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
          outline: "none"
        },
        "& .MuiDataGrid-cell--editing": {
          outline: "none"
        },
        "& .row-to-delete": {
          backgroundColor: "rgba(255, 0, 0, 0.1)",
          "&:hover": { backgroundColor: "rgba(255, 0, 0, 0.2)" }
        }
      }}
      disableRowSelectionOnClick
      showExploreDataButtonOnFooter={false}
    />
  );
};
